/**
 * Asset Management · Condition & criticality.
 *
 * Where the risk is: every assessed asset placed by how much it matters
 * (criticality, rows) and how it is (health band, columns), with risk the
 * product. The unassessed are counted beside the matrix, never folded into
 * "very good" — on the real estate they are most of the register, and that is
 * the first thing an asset manager needs to know.
 */
import { Link } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmCondition, useAmRefreshHealth } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, ErrorState, Skeleton, Table } from "../../design/components";
import { BAND_LABEL, riskOf, type AmCondition } from "../../domain/assetManagement";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { ConditionSurveyCard } from "../entryForms/ConditionSurveyCard";
import { Coverage, Criticality, errorText, HealthChip, s, SUITE, Tiles, TrendLine } from "./shared";

/** Risk 1–25 to a colour: the status palette, never a series colour. */
function riskColour(risk: number): string {
  if (risk >= 15) return "var(--nogo)";
  if (risk >= 10) return "var(--p2)";
  if (risk >= 6) return "var(--caution)";
  return "var(--go)";
}

export function AssetConditionPage() {
  const session = useSession();
  const q = useAmCondition();
  const refresh = useAmRefreshHealth();
  const c = q.data;
  const canEdit = hasPermission(session.principal, PERMISSION.assetWrite);

  useScreenContext(
    c
      ? {
          route: "/asset-condition",
          title: "Condition and risk",
          summary: [
            t("{a} of {n} assets assessed; {u} have no data yet.", {
              a: c.assessed,
              n: c.assets,
              u: c.unassessed,
            }),
            t("{h} assessed assets are high risk (15 or more of 25).", { h: c.highRisk }),
            c.topRisks[0]
              ? t("Highest risk: {tag}, criticality {c}, health {score}.", {
                  tag: c.topRisks[0].tag,
                  c: c.topRisks[0].criticality,
                  score: c.topRisks[0].healthScore,
                })
              : t("No assessed asset is at risk."),
          ],
          offers: [
            { label: t("What is highest risk?"), ask: "which assets are highest risk?" },
            {
              label: t("Which types are in the worst condition?"),
              ask: "which asset types are in the worst condition?",
            },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Condition and risk"
      actions={
        canEdit ? (
          <Button onClick={() => refresh.mutate()} disabled={refresh.isPending} data-testid="am-refresh">
            {refresh.isPending ? t("Recalculating…") : t("Recalculate health")}
          </Button>
        ) : undefined
      }
    >
      {refresh.error && <p className={s.err}>{errorText(refresh.error)}</p>}
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {c && <Body c={c} />}
    </Page>
  );
}

function Body({ c }: { c: AmCondition }) {
  const mean = c.assessed
    ? Math.round((c.byType.reduce((sum, ty) => sum + (ty.meanHealth ?? 0) * ty.assessed, 0) / c.assessed) * 10) / 10
    : null;
  const poor = (c.bands["1"] ?? 0) + (c.bands["2"] ?? 0);
  return (
    <>
      <Tiles
        items={[
          {
            key: "assessed",
            label: t("Assessed"),
            value: c.assessed.toLocaleString("en"),
            sub: t("of {n} assets", { n: c.assets.toLocaleString("en") }),
          },
          { key: "mean", label: t("Mean health"), value: mean ?? "—", sub: t("0–100, assessed assets only") },
          { key: "poor", label: t("Poor or very poor"), value: poor, tone: poor ? "warn" : "good" },
          {
            key: "high",
            label: t("High risk"),
            value: c.highRisk,
            sub: t("risk 15 or more of 25"),
            tone: c.highRisk ? "bad" : "good",
          },
        ]}
      />
      <Coverage lines={[{ label: t("have enough recorded to assess"), of: c.assets, n: c.assessed }]} />
      <div className={s.split}>
        <Card title={t("Criticality × health")} sub={t("Asset count per cell; color shows risk.")}>
          <Matrix c={c} />
        </Card>
        <Card title={t("Health by asset type")} sub={t("Worst first; unassessed types last.")}>
          <Table>
            <thead>
              <tr>
                <th>{t("Type")}</th>
                <th className={s.num}>{t("Assets")}</th>
                <th className={s.num}>{t("Assessed")}</th>
                <th className={s.num}>{t("Mean health")}</th>
                <th className={s.num}>{t("Poor")}</th>
              </tr>
            </thead>
            <tbody>
              {c.byType.map((ty) => (
                <tr key={ty.assetType}>
                  <td>{ty.name}</td>
                  <td className={s.num}>{ty.assets}</td>
                  <td className={s.num}>{ty.assessed}</td>
                  <td className={s.num}>{ty.meanHealth ?? "—"}</td>
                  <td className={s.num}>{ty.poor}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>
      <Card title={t("Highest risk")} sub={t("Ranked by criticality and condition, then lowest health.")}>
        {c.topRisks.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Tag")}</th>
                <th>{t("Name")}</th>
                <th>{t("Zone")}</th>
                <th>{t("Criticality")}</th>
                <th>{t("Health")}</th>
                <th className={s.num}>{t("Risk")}</th>
              </tr>
            </thead>
            <tbody>
              {c.topRisks.map((r) => (
                <tr key={r.id}>
                  <td className={s.mono}>
                    <Link to={`/asset-register/${encodeURIComponent(r.id)}`}>{r.tag}</Link>
                  </td>
                  <td>{r.name}</td>
                  <td className={s.mono}>{r.zoneCode ?? "—"}</td>
                  <td>
                    <Criticality value={r.criticality} />
                  </td>
                  <td>
                    <HealthChip band={r.healthBand} score={r.healthScore} />
                  </td>
                  <td className={s.num} style={{ color: riskColour(r.risk), fontWeight: 700 }}>
                    {r.risk}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No asset is assessed yet, so no risk can be ranked.")}</EmptyState>
        )}
      </Card>
      {/* build 6B: the surveyed score health is worked out from, entered here */}
      <ConditionSurveyCard recent={c.recentSurveys} surveyed={c.surveyed} assets={c.assets} />
      {c.trend.length > 1 && (
        <Card title={t("Mean health by month")} sub={t("Assessed assets, last twelve months.")}>
          <TrendLine
            label={t("Mean health by month")}
            points={c.trend.map((p) => ({ label: p.month.slice(0, 7), value: p.meanHealth }))}
          />
        </Card>
      )}
      <p className={s.note}>
        {t("How it is worked out:")} {c.method}
      </p>
    </>
  );
}

function Matrix({ c }: { c: AmCondition }) {
  const bands = [1, 2, 3, 4, 5];
  return (
    <div className={s.matrixWrap}>
      <table className={s.matrix} data-testid="am-matrix">
        <thead>
          <tr>
            <th>{t("Criticality")}</th>
            {bands.map((b) => (
              <th key={b} scope="col">
                {t(BAND_LABEL[b])}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[5, 4, 3, 2, 1].map((crit) => (
            <tr key={crit}>
              <th scope="row">{crit}</th>
              {bands.map((b) => {
                const n = c.matrix[crit - 1]?.[b - 1] ?? 0;
                const risk = riskOf(crit, b);
                return (
                  <td
                    key={b}
                    className={`${s.cell} ${n ? "" : s.cellEmpty}`}
                    style={n ? { background: riskColour(risk) } : undefined}
                    title={t("Criticality {c}, {band}: {n} assets, risk {r}", {
                      c: crit,
                      band: t(BAND_LABEL[b]),
                      n,
                      r: risk,
                    })}
                  >
                    {n || "·"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={s.note}>
        {t("Not in the matrix: {n} unassessed assets.", { n: c.unassessed.toLocaleString("en") })}
      </p>
    </div>
  );
}
